/**
 * SmartSync / Odyssey programming-file scenario utilities.
 *
 * The host app pre-computes the SmartSync scenario so the SR Portal can
 * display the correct Programming File Request section without making its
 * own patient-devices API call.
 */

/**
 * Compute the SmartSync scenario for a Sound Processor being serviced.
 *
 * Decision table
 * ─────────────────────────────────────────────────────────────────────────
 * SP smartSyncCompatibility          Compatible implant condition
 * ─────────────────────────────────  ────────────────────────────────────
 * "Compatible"                       (no check)           → ODYSSEY_SMART_SYNC
 * "Smart Sync compatible"            (no check)           → ODYSSEY_SMART_SYNC
 * "Compatible with Smart Sync Implant"  SP earSide Unknown  → ODYSSEY_UNDETERMINED
 * "Compatible with Smart Sync Implant"  No active Registered implant with
 *                                       "Compatible with Smart Sync Implant" → NOT_APPLICABLE
 * "Compatible with Smart Sync Implant"  Active Registered implant with
 *                                       "Compatible with Smart Sync Implant" on same ear → ODYSSEY_SMART_SYNC
 * "Compatible with Smart Sync Implant"  Active Registered implant with
 *                                       "Compatible with Smart Sync Implant" on different ear → ODYSSEY_UNDETERMINED
 * "Not compatible" or undefined      (no check)           → NOT_APPLICABLE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * @param {object} rawSpDevice - The raw SP device entry from rawEquipmentList.devices.
 *   Must contain at least `smartSyncCompatibility` (string|undefined) and
 *   `earSide` ("Left" | "Right" | "Unknown").
 * @param {Array<object>} allRawDevices - Full rawEquipmentList.devices array.
 *   Each entry is expected to have `productType`, `isActive`, `status`,
 *   `earSide`, and `smartSyncCompatibility`.
 * @returns {"ODYSSEY_SMART_SYNC" | "ODYSSEY_UNDETERMINED" | "NOT_APPLICABLE"}
 */
export const computeSmartSyncScenario = (
  rawSpDevice,
  allRawDevices,
) => {
  const spCompatibility = rawSpDevice?.smartSyncCompatibility;

  if (!spCompatibility || spCompatibility === 'Not compatible') {
    return 'NOT_APPLICABLE';
  }

  if (
    spCompatibility === 'Compatible' ||
    spCompatibility === 'Smart Sync compatible'
  ) {
    return 'ODYSSEY_SMART_SYNC';
  }

  // spCompatibility === 'Compatible with Smart Sync Implant'
  const spEarSide = rawSpDevice.earSide;

  // Unknown ear side → cannot confirm a same-ear implant
  if (!spEarSide || spEarSide === 'Unknown') {
    return 'ODYSSEY_UNDETERMINED';
  }

  const compatibleActiveImplants = (allRawDevices || []).filter(
    (d) =>
      d.productType === 'Implant' &&
      d.isActive === true &&
      d.status === 'Registered' &&
      d.smartSyncCompatibility ===
        'Compatible with Smart Sync Implant',
  );

  if (compatibleActiveImplants.length === 0) {
    return 'NOT_APPLICABLE';
  }

  const hasSameEarCompatibleImplant = compatibleActiveImplants.some(
    (d) => d.earSide === spEarSide,
  );

  return hasSameEarCompatibleImplant
    ? 'ODYSSEY_SMART_SYNC'
    : 'ODYSSEY_UNDETERMINED';
};
